import { type FC, type ForwardedRef } from 'react';

import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { MaterialDetailsModal } from '$/components/core/Collection/MaterialDetailsModal';
import { DataSortingRow } from '$/components/core/Collection/MaterialPresentation/DataSortingRow';
import { FanPresentation } from '$/components/core/Collection/MaterialPresentation/FanPresentation';
import { GridPresentation } from '$/components/core/Collection/MaterialPresentation/GridPresentation';
import { ListPresentation } from '$/components/core/Collection/MaterialPresentation/ListPresentation';
import { useInfiniteMaterials } from '$/hooks/data/useInfiniteMaterials';
import { useIntersectionObserver } from '$/hooks/useIntersectionObserver';
import {
  type Material,
  type MaterialType,
} from '$/services/usecases/materials/mapper/materials';
import { useCollectionStore } from '$/stores/useCollectionStore';

interface Props {
  type: MaterialType;
  scrollRefs?: ForwardedRef<HTMLDivElement>[];
  hideFavoriteButton?: boolean;
  isFullscreenMode?: boolean;
  hideFilterTags?: boolean;
  onSelect: (material: Material) => void;
}

export const MaterialPresentation: FC<Props> = ({
  type,
  scrollRefs,
  onSelect,
  hideFavoriteButton = false,
  isFullscreenMode = false,
  hideFilterTags = false,
}) => {
  const { hasPermission } = useAuthorization();
  const selectedViewMode = useCollectionStore.useSelectedViewMode();

  const [{ isLoading, fetchNextPage, isFetchingNextPage }, , materials, count] =
    useInfiniteMaterials({
      type,
      limit: 100,
      hideProMaterials: !hasPermission('View_All_Collections'),
    });

  const observer = useIntersectionObserver(
    (intersecting) => intersecting && fetchNextPage(),
    { disabled: !materials || isFetchingNextPage || materials?.length === 0 },
  );

  return (
    <>
      {!hideFilterTags && (
        <DataSortingRow
          amount={count}
          isLoading={isLoading}
          scrollRefs={scrollRefs}
        />
      )}

      {selectedViewMode === 'grid' && (
        <GridPresentation
          isLoading={isLoading || isFetchingNextPage}
          observer={observer}
          materials={materials ?? []}
          type={type}
          setActiveMaterial={onSelect}
          hideFavoriteButton={hideFavoriteButton}
          isFullscreenMode={isFullscreenMode}
        />
      )}

      {selectedViewMode === 'list' && (
        <ListPresentation
          isLoading={isLoading || isFetchingNextPage}
          observer={observer}
          materials={materials ?? []}
          type={type}
          setActiveMaterial={onSelect}
          hideFavoriteButton={hideFavoriteButton}
        />
      )}

      {selectedViewMode === 'fan' && (
        <FanPresentation
          isLoading={isLoading || isFetchingNextPage}
          observer={observer}
          materials={materials ?? []}
          type={type}
          setActiveMaterial={onSelect}
          hideFavoriteButton={hideFavoriteButton}
        />
      )}

      <MaterialDetailsModal
        returnFocusOnClose={false}
        blockScrollOnMount={false}
      />
    </>
  );
};
